import { Avatar, Flex, Image, Rate } from "antd";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../../components/navbar";
import Footer from "../../components/footer";
import { useTranslation } from "react-i18next";
import { CTA, ImageContainer, LandingContainer, SectionTitle, Welcome, WelcomeContent, WelcomeDescription, WelcomeTitle } from "../../styled-components/landing";
import { useQuery } from "@apollo/client";
import { RETRIEVE_REVIEWS } from "../../apollo/query/review.query";
import { format } from "date-fns";
import { RETRIEVE_TOP_PICKS } from "../../apollo/query/product.query";
import { ProductContainer, ProductsContainer } from "../../styled-components/carta";

const Landing = () => {
    /** Retrieve param from url */
    const [searchParams] = useSearchParams();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { data: reviews } = useQuery(RETRIEVE_REVIEWS, { fetchPolicy: 'no-cache', pollInterval: 5000, variables: { nIDRestaurant: parseInt(searchParams.get('nIDRestaurant') ?? '-1' ) }} );
    const { data: topPicks } = useQuery(RETRIEVE_TOP_PICKS, { variables: { nIDRestaurant: parseInt(searchParams.get('nIDRestaurant') ?? '-1' ) } });
    
    const redirectToMenu = () => {
        navigate(`/home?nIDRestaurant=${parseInt(searchParams.get('nIDRestaurant') ?? '-1' )}`);
    }
    
    const redirectToBook = () => {
        navigate('/book', { state: { nIDRestaurant: parseInt(searchParams.get('nIDRestaurant') ?? '-1' ) }} );
    }

    return (
        <LandingContainer>
            <Welcome>
                <ImageContainer>
                    <img
                        src={`https://api.ams-carriot-restaurant.com/restaurant-image/${parseInt(searchParams.get('nIDRestaurant') ?? '-1')}?timestamp=${new Date().getTime()}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
                        alt="AMS Restaurant Interior"
                    />
                    <div style={{ position: 'absolute', inset: 0, backgroundColor: 'black', opacity: 0.5 }} />
                </ImageContainer>

                <Navbar nIDRestaurant={parseInt(searchParams.get('nIDRestaurant') ?? '-1' )} />

                <WelcomeContent vertical justify="center" align="flex-start">
                    <WelcomeTitle> { t('landing.title') } </WelcomeTitle>
                    <WelcomeDescription> { t('landing.description') } </WelcomeDescription>
                    <Flex gap={30} wrap='wrap' style={{ marginTop: 20 }}>
                        <CTA type="primary" size="large" onClick={redirectToBook}>
                            { t('buttons.book-table') }
                        </CTA>
                        <CTA size="large" onClick={redirectToMenu} style={{ border: '1px solid white', backgroundColor: 'transparent', color: 'white' }}>
                            { t('buttons.view-menu') }
                        </CTA>
                    </Flex>
                </WelcomeContent>
            </Welcome>

            { /* Top dishes */}
            <Flex vertical align="center" justify="center" style={{ paddingTop: 20, backgroundColor: '#1F1F1F'}}>
                <SectionTitle> { t('landing.top-picks') } </SectionTitle>
                <ProductsContainer>
                    {topPicks?.data?.map((dish: any) => (
                        <ProductContainer key={dish.nID}>
                            <Flex vertical gap={20} justify='space-between' style={{ paddingBottom: 10, paddingTop: 10, maxWidth: 150 }}>
                                <span style={{ color: 'white', fontFamily: 'Roboto-Bold', fontSize: 15 }}> { dish.languages['name-' + i18n.language] } </span>
                                <span style={{ color: 'white', fontFamily: 'Roboto-Light', lineClamp: 2, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 13 }}> { dish.languages['desc-' + i18n.language] } </span>
                                <span style={{ color: '#0d9ce8', fontFamily: 'Roboto-Bold', fontSize: 14 }}> { dish.nPrice.toFixed(2) }€ </span>
                            </Flex>
                            <Flex align='center'>
                                <Image
                                    src={dish.sImages[0]}
                                    alt={dish.languages['name-' + i18n.language]}
                                    style={{ height: 100, width: 100, objectFit: 'cover' }}
                                />
                            </Flex>
                        </ProductContainer>
                    ))}
                </ProductsContainer>
            </Flex>

            {/* Testimonials Section */}
            { reviews?.data?.length ? 
                <Flex vertical align="center" justify="center" style={{ padding: 50, paddingTop: 20, backgroundColor: '#1A1A1A'}}>
                    <SectionTitle> { t('landing.testimonis') } </SectionTitle>
                    <Swiper
                        modules={[Pagination, Autoplay]}
                        spaceBetween={30}
                        pagination={{ clickable: true }}
                        autoplay={{ delay: 5000 }}
                        style={{ width: "90%", height: 200 }}
                        breakpoints={{
                            320: { slidesPerView: 1 },
                            640: { slidesPerView: 2 },
                            1024: { slidesPerView: 3 },
                            1440: { slidesPerView: 4 }
                        }}
                    >
                        { reviews?.data?.map((review: any) => (
                            <SwiperSlide key={review.nID}>
                                <div style={{ backgroundColor: '#1E1E1E', padding: '5px 20px', borderRadius: 5, boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px' }}>
                                    <Flex align="center" justify="center" gap={20} style={{ marginBottom: 10 }}>
                                        <div style={{ width: '100%', padding: '10px 15px'}}>
                                            <Flex justify="flex-start" align="center" gap={20}>
                                                <Avatar style={{ backgroundColor: '#4B5563' }} />
                                                <Flex vertical gap={0}>
                                                    <h3 style={{ fontSize: '0.9rem', fontFamily: 'Roboto-Thin', margin: 0 }}> { review.sCustomerName} </h3>
                                                    <h3 style={{ fontSize: '0.8rem', fontFamily: 'Roboto-Thin', margin: 0, marginBottom: 20 }}> { format(new Date(review.dDate), 'dd-MM-yyyy') } </h3>
                                                </Flex>
                                            </Flex>
                                            <Rate
                                                disabled
                                                defaultValue={ review.rating }
                                                style={{ color: '#4FB3E8'}}
                                            />
                                        </div>
                                    </Flex>
                                    <p style={{ color: 'lightgrey', fontSize: '.8rem', fontFamily: 'Roboto-Thin', lineHeight: 1.5 }}>{ review.sReview}</p>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </Flex>
            :
                <Flex vertical align="center" justify="center" style={{ padding: 50, paddingTop: 20, backgroundColor: '#1A1A1A'}}>
                    <SectionTitle> { t('landing.testimonis') } </SectionTitle>
                    <span style={{ fontFamily: 'Suburban', fontSize: '1.9rem' }}> { t('label.no-reviews') } </span>
                </Flex>
            }
            {/* Footer */}
            <Footer nIDRestaurant={parseInt(searchParams.get('nIDRestaurant') ?? '-1' )} />
        </LandingContainer>
  );
};

export default Landing;
